/**
 * The application's functional icon set — one stroke style (24px grid, 1.8px
 * round stroke, `currentColor`) for every glyph that is not part of the rail
 * (`NavIcon`) or a page's own pictograms.
 *
 * Use this instead of Unicode symbols (✕ ✓ ✉ ⚠ ↗ →): those render in whatever
 * font the platform falls back to, at an optical size and weight that never
 * matches Inter, and screen readers announce them by name.
 *
 * Decorative by default (`aria-hidden`). Pass `title` only when the icon is the
 * sole carrier of meaning, e.g. an icon-only button without `aria-label`.
 */

import type { ReactNode } from 'react';

export type IconName =
  | 'arrow-left'
  | 'arrow-right'
  | 'check'
  | 'chevron-down'
  | 'chevron-right'
  | 'clock'
  | 'close'
  | 'code'
  | 'divider'
  | 'download'
  | 'edit'
  | 'external'
  | 'info'
  | 'link'
  | 'list-bullet'
  | 'list-ordered'
  | 'mail'
  | 'minus'
  | 'plus'
  | 'quote'
  | 'refresh'
  | 'search'
  | 'table'
  | 'trash'
  | 'warning';

const PATHS: Readonly<Record<IconName, ReactNode>> = {
  'arrow-left': <path d="M19 12H5m6-6-6 6 6 6" />,
  'arrow-right': <path d="M5 12h14m-6-6 6 6-6 6" />,
  check: <path d="m5 12.5 4.5 4.5L19 7" />,
  'chevron-down': <path d="m6 9 6 6 6-6" />,
  'chevron-right': <path d="m9 6 6 6-6 6" />,
  clock: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 7.5V12l3 2" />
    </>
  ),
  close: <path d="M6.5 6.5l11 11m0-11-11 11" />,
  code: <path d="m8.5 7.5-5 4.5 5 4.5m7-9 5 4.5-5 4.5" />,
  divider: <path d="M4 12h16M8 7.5h8m-8 9h8" />,
  download: <path d="M12 4v11m-5-5 5 5 5-5M5 20h14" />,
  edit: <path d="M4.5 19.5h3.8L19 8.8 15.2 5 4.5 15.7v3.8Z" />,
  external: <path d="M14 4.5h5.5V10m0-5.5L11 13m7 1.5V19a.5.5 0 0 1-.5.5h-12A.5.5 0 0 1 5 19V7a.5.5 0 0 1 .5-.5H10" />,
  info: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 11v5.2m0-8.4v.1" />
    </>
  ),
  link: <path d="M10.5 13.5a3.5 3.5 0 0 0 5 0l3-3a3.5 3.5 0 0 0-5-5l-1 1m-1 7a3.5 3.5 0 0 0-5 0l-3 3a3.5 3.5 0 0 0 5 5l1-1" />,
  'list-bullet': <path d="M9.5 6.5h10m-10 5.5h10m-10 5.5h10M5 6.5v.1M5 12v.1m0 5.4v.1" />,
  'list-ordered': <path d="M10 6.5h9.5M10 12h9.5M10 17.5h9.5M4.5 5 6 4v5m-1.5 4.5a1.2 1.2 0 1 1 2 1L4.5 17H7" />,
  mail: (
    <>
      <rect x="3.5" y="5.5" width="17" height="13" rx="1.5" />
      <path d="m4 6.5 8 6 8-6" />
    </>
  ),
  minus: <path d="M5 12h14" />,
  plus: <path d="M12 5v14M5 12h14" />,
  quote: <path d="M5 7h5v5c0 2.5-1.5 4-4 5M14 7h5v5c0 2.5-1.5 4-4 5" />,
  refresh: <path d="M19.5 11.5a7.5 7.5 0 1 0-2.2 5.3M19.5 5v6.5H13" />,
  search: (
    <>
      <circle cx="10.5" cy="10.5" r="6" />
      <path d="m15 15 5 5" />
    </>
  ),
  table: (
    <>
      <rect x="3.5" y="5" width="17" height="14" rx="1.5" />
      <path d="M3.5 10h17m-17 4.5h17M10 10v9" />
    </>
  ),
  trash: <path d="M4.5 7h15M9.5 7V4.5h5V7m-8 0 .8 12.5h9.4L17.5 7" />,
  warning: <path d="M12 4 20.5 19h-17L12 4Zm0 5.8v4.4m0 2.6v.1" />,
};

export interface IconProps {
  name: IconName;
  /** Edge length in px. Default 16 — the size that sits on a 13–14px text line. */
  size?: number;
  className?: string;
  /** Accessible name; omit for decorative use (the default). */
  title?: string;
}

export function Icon({ name, size = 16, className, title }: IconProps): ReactNode {
  return (
    <svg
      className={className ? `icon ${className}` : 'icon'}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      /* Scaled down, the 1.8 stroke thins to a hairline; small sizes get more. */
      strokeWidth={size <= 12 ? 2.2 : 1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden={title ? undefined : true}
      role={title ? 'img' : undefined}
      focusable="false"
    >
      {title ? <title>{title}</title> : null}
      {PATHS[name]}
    </svg>
  );
}

export default Icon;
